import React, { useEffect, useState } from 'react';

export default function TextRotationControl({ value, onChange }) {
  const [input, setInput] = useState(String(value ?? 0));

  useEffect(() => {
    const asString = String(value ?? 0);
    if (asString !== input) setInput(asString);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const clamp = (n) => Math.min(360, Math.max(-360, n));

  const commit = (str) => {
    const n = parseInt(str, 10);
    const safe = Number.isFinite(n) ? clamp(n) : 0;
    setInput(String(safe));
    onChange?.(safe);
  };

  const handleChange = (e) => {
    const v = e.target.value; // keep string to allow interim states
    if (/^-?\d*$/.test(v)) {
      setInput(v);
      if (/^-?\d+$/.test(v)) {
        onChange?.(clamp(parseInt(v, 10)));
      }
    }
  };

  const handleBlur = () => commit(input);

  const toggleSign = () => {
    // If empty, start with "-" so user can type digits next
    if (input === '') {
      setInput('-');
      return;
    }
    if (input === '-') {
      setInput(''); // go back to empty
      return;
    }
    const next = input.startsWith('-') ? input.slice(1) : `-${input}`;
    setInput(next);
    if (/^-?\d+$/.test(next)) onChange?.(clamp(parseInt(next, 10)));
  };

  const step = (delta) => {
    const n = Number.isFinite(parseInt(input, 10)) ? parseInt(input, 10) : 0;
    const next = clamp(n + delta);
    setInput(String(next));
    onChange?.(next);
  };

  return (
    <div className="flex items-center gap-2">
      <label htmlFor="font-rotation" className="text-sm text-gray-600">
        Rotate
      </label>

      <div className="flex items-center gap-1">
        {/* Sign toggle */}
        <button
          type="button"
          onClick={toggleSign}
          className="h-8 px-2 border rounded text-sm"
          aria-label="Toggle sign"
          title="Toggle sign (±)"
        >
          ±
        </button>

        {/* Number input: keeps numeric keypad on mobile */}
        <input
          id="font-rotation"
          type="number"
          min={-360}
          max={360}
          step={1}
          inputMode="numeric" // numeric keypad
          pattern="-?[0-9]*"
          value={input}
          onChange={handleChange}
          onBlur={handleBlur}
          className="w-20 border rounded p-1 text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          aria-label="Rotation in degrees"
        />

        {/* Small steppers */}
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => step(-1)}
            className="h-8 px-2 border rounded text-sm"
            aria-label="Decrease rotation by 1 degree"
            title="-1°"
          >
            −1°
          </button>
          <button
            type="button"
            onClick={() => step(1)}
            className="h-8 px-2 border rounded text-sm"
            aria-label="Increase rotation by 1 degree"
            title="+1°"
          >
            +1°
          </button>
        </div>
      </div>

      <span>°</span>
    </div>
  );
}
